/**
 * Canvas-to-HTML Serializer
 * Converts canvas layout data into a complete HTML document for backend PDF rendering.
 * The backend uses Puppeteer to render html_template and replaces {{variables}} via regex.
 */
import {
  CanvasLayoutData,
  CanvasElement,
  CanvasElementType,
  TextCanvasElement,
  VariableCanvasElement,
  ImageCanvasElement,
  ShapeCanvasElement,
  LineCanvasElement,
  QRCodeCanvasElement,
  SignatureCanvasElement,
  CanvasConfig
} from '@/types/certificate-template.types';

/** Convert a TextStyle + ElementPosition into an inline CSS string */
function buildPositionedStyle(el: CanvasElement): string {
  const { x, y, width, height, rotation, zIndex } = el.position;
  const parts = [
    'position:absolute',
    `left:${Math.round(x)}px`,
    `top:${Math.round(y)}px`,
    `width:${Math.round(width)}px`,
    `height:${Math.round(height)}px`
  ];
  if (zIndex) parts.push(`z-index:${zIndex}`);
  if (rotation) parts.push(`transform:rotate(${rotation}deg)`);
  if (el.opacity !== undefined && el.opacity < 1) parts.push(`opacity:${el.opacity}`);
  return parts.join(';');
}

/** Build text-specific inline styles */
function buildTextStyle(el: TextCanvasElement | VariableCanvasElement): string {
  const s = el.style;
  const justify = s.textAlign === 'center' ? 'center'
    : s.textAlign === 'right' ? 'flex-end' : 'flex-start';
  return [
    `font-family:${s.fontFamily}`,
    `font-size:${s.fontSize}px`,
    `font-weight:${s.fontWeight}`,
    `font-style:${s.fontStyle}`,
    `text-decoration:${s.textDecoration}`,
    `text-align:${s.textAlign}`,
    `line-height:${s.lineHeight}`,
    `letter-spacing:${s.letterSpacing}px`,
    `color:${s.color}`,
    s.backgroundColor ? `background-color:${s.backgroundColor}` : '',
    s.borderRadius ? `border-radius:${s.borderRadius}px` : '',
    'display:flex',
    'align-items:center',
    `justify-content:${justify}`,
    'overflow:hidden'
  ].filter(Boolean).join(';');
}

/** Serialize a single canvas element to HTML */
export function elementToHtml(el: CanvasElement): string {
  if (!el.visible) return '';

  const posStyle = buildPositionedStyle(el);

  switch (el.type) {
    case CanvasElementType.TEXT: {
      const textEl = el as TextCanvasElement;
      // one line, like the canvas shows it (overflow:visible so a long title isn't clipped)
      const textStyle = `${buildTextStyle(textEl)};white-space:nowrap;overflow:visible`;
      return `<div style="${posStyle};${textStyle}">${escapeHtml(textEl.content)}</div>`;
    }

    case CanvasElementType.VARIABLE: {
      const varEl = el as VariableCanvasElement;
      if (varEl.variableKey.endsWith('_logo_url')) {
        return `<div style="${posStyle};overflow:hidden"><img src="{{${varEl.variableKey}}}" alt="" style="width:100%;height:100%;object-fit:contain"/></div>`;
      }
      const textStyle = buildTextStyle(varEl);
      return `<div style="${posStyle};${textStyle}">{{${varEl.variableKey}}}</div>`;
    }

    case CanvasElementType.IMAGE: {
      const imgEl = el as ImageCanvasElement;
      const imgStyle = `width:100%;height:100%;object-fit:${imgEl.fit}`;
      const borderStyle = imgEl.border
        ? `;border:${imgEl.border.width}px ${imgEl.border.style} ${imgEl.border.color};border-radius:${imgEl.border.radius || 0}px`
        : '';
      return `<div style="${posStyle}${borderStyle}"><img src="${escapeAttr(imgEl.src)}" alt="${escapeAttr(imgEl.alt || '')}" style="${imgStyle}"/></div>`;
    }

    case CanvasElementType.SHAPE: {
      const shapeEl = el as ShapeCanvasElement;
      const borderRadius = shapeEl.shapeType === 'circle' || shapeEl.shapeType === 'ellipse'
        ? '50%' : `${shapeEl.border?.radius || 0}px`;
      const borderStyle = shapeEl.border
        ? `;border:${shapeEl.border.width}px ${shapeEl.border.style} ${shapeEl.border.color}`
        : '';
      return `<div style="${posStyle};background:${shapeEl.fill};border-radius:${borderRadius}${borderStyle}"></div>`;
    }

    case CanvasElementType.LINE: {
      const lineEl = el as LineCanvasElement;
      const dashStyle = lineEl.strokeStyle === 'dashed' ? 'dashed'
        : lineEl.strokeStyle === 'dotted' ? 'dotted' : 'solid';
      const isVertical = el.position.height > el.position.width;
      if (isVertical) {
        return `<div style="${posStyle};width:0;border-left:${lineEl.strokeWidth}px ${dashStyle} ${lineEl.strokeColor}"></div>`;
      }
      return `<div style="${posStyle};height:0;border-top:${lineEl.strokeWidth}px ${dashStyle} ${lineEl.strokeColor}"></div>`;
    }

    case CanvasElementType.QR_CODE: {
      const qrEl = el as QRCodeCanvasElement;
      return `<div style="${posStyle};display:flex;align-items:center;justify-content:center;background:${qrEl.backgroundColor}"><img src="{{${qrEl.dataKey}}}" alt="QR Code" style="width:${qrEl.size}px;height:${qrEl.size}px"/></div>`;
    }

    case CanvasElementType.SIGNATURE: {
      const sigEl = el as SignatureCanvasElement;
      if (sigEl.signatureType === 'image' && sigEl.src) {
        return `<div style="${posStyle};display:flex;flex-direction:column;align-items:center;justify-content:center"><img src="${escapeAttr(sigEl.src)}" alt="Signature" style="max-width:100%;max-height:70%"/>${sigEl.showLabel && sigEl.label ? `<div style="margin-top:4px;font-size:11px;color:#6B7280;border-top:1px solid #E5E7EB;padding-top:4px">${escapeHtml(sigEl.label)}</div>` : ''}</div>`;
      }
      const sigStyle = sigEl.style
        ? `font-family:${sigEl.style.fontFamily};font-size:${sigEl.style.fontSize}px;color:${sigEl.style.color}`
        : 'font-family:"Dancing Script",cursive;font-size:24px;color:#111827';
      return `<div style="${posStyle};display:flex;flex-direction:column;align-items:center;justify-content:center"><div style="${sigStyle}">${escapeHtml(sigEl.text || '')}</div>${sigEl.showLabel && sigEl.label ? `<div style="margin-top:4px;font-size:11px;color:#6B7280;border-top:1px solid #E5E7EB;padding-top:4px">${escapeHtml(sigEl.label)}</div>` : ''}</div>`;
    }

    default:
      return '';
  }
}

/** Build background CSS for the body */
function buildBackgroundCss(config: CanvasConfig): string {
  const { background } = config;
  const parts: string[] = [];

  if (background.type === 'solid') {
    parts.push(`background:${background.color || '#FFFFFF'}`);
  } else if (background.type === 'gradient' && background.gradient) {
    const { gradient } = background;
    const stops = gradient.colors.map(c => `${c.color} ${c.position}%`).join(',');
    if (gradient.type === 'linear') {
      parts.push(`background:linear-gradient(${gradient.angle || 0}deg,${stops})`);
    } else {
      parts.push(`background:radial-gradient(circle,${stops})`);
    }
  } else if (background.type === 'image' && background.image?.src) {
    parts.push(`background-image:url(${background.image.src})`);
    parts.push(`background-size:${background.image.fit === 'tile' ? 'auto' : background.image.fit}`);
    parts.push(`background-repeat:${background.image.fit === 'tile' ? 'repeat' : 'no-repeat'}`);
    parts.push('background-position:center');
  }

  return parts.join(';');
}

/** Escape HTML special characters */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Escape attribute value */
function escapeAttr(text: string): string {
  return text.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Collect unique font families used in the layout */
function collectFonts(elements: CanvasElement[]): string[] {
  const fonts = new Set<string>();
  elements.forEach(el => {
    if (el.type === CanvasElementType.TEXT || el.type === CanvasElementType.VARIABLE) {
      const textEl = el as TextCanvasElement | VariableCanvasElement;
      const family = textEl.style.fontFamily.split(',')[0].trim().replace(/"/g, '');
      if (family && !['Georgia', 'Arial', 'Helvetica', 'Times New Roman', 'Courier New'].includes(family)) {
        fonts.add(family);
      }
    }
  });
  return Array.from(fonts);
}

/**
 * Build a complete HTML document from canvas layout data.
 * This HTML is sent to the backend as html_template for Puppeteer PDF rendering.
 */
export function buildCanvasHtml(layoutData: CanvasLayoutData): string {
  const { canvas, elements } = layoutData;

  // Sort elements by zIndex for proper layering
  const sortedElements = [...elements].sort(
    (a, b) => (a.position.zIndex || 0) - (b.position.zIndex || 0)
  );

  // Collect Google Fonts
  const googleFonts = collectFonts(elements);
  const fontLinks = googleFonts.length > 0
    ? `<link href="https://fonts.googleapis.com/css2?${googleFonts.map(f => `family=${f.replace(/\s/g, '+')}:wght@400;600;700`).join('&')}&display=swap" rel="stylesheet">`
    : '';

  // Build border CSS
  const borderCss = canvas.border
    ? `border:${canvas.border.width}px ${canvas.border.style} ${canvas.border.color};${canvas.border.radius ? `border-radius:${canvas.border.radius}px` : ''}`
    : '';

  // Build decorative border CSS + HTML
  let decorativeBorderCss = '';
  let decorativeBorderHtml = '';
  const db = canvas.decorativeBorder;
  if (db) {
    if (db.outer?.enabled) {
      const o = db.outer;
      decorativeBorderCss += `.outer-border{position:absolute;top:${o.inset.top}px;left:${o.inset.left}px;right:${o.inset.right}px;bottom:${o.inset.bottom}px;border:${o.width}px ${o.style} ${o.color};pointer-events:none;}\n`;
      decorativeBorderHtml += '<div class="outer-border"></div>\n';
    }
    if (db.inner?.enabled) {
      const n = db.inner;
      decorativeBorderCss += `.inner-border{position:absolute;top:${n.inset.top}px;left:${n.inset.left}px;right:${n.inset.right}px;bottom:${n.inset.bottom}px;border:${n.width}px ${n.style} ${n.color};pointer-events:none;}\n`;
      decorativeBorderHtml += '<div class="inner-border"></div>\n';
    }
    if (db.corners?.enabled) {
      const c = db.corners;
      decorativeBorderCss += `.corner{position:absolute;width:${c.size}px;height:${c.size}px;border-color:${c.color};border-style:solid;pointer-events:none;}\n`;
      decorativeBorderCss += `.corner-tl{top:${c.inset.top}px;left:${c.inset.left}px;border-width:${c.width}px 0 0 ${c.width}px;}\n`;
      decorativeBorderCss += `.corner-tr{top:${c.inset.top}px;right:${c.inset.right}px;border-width:${c.width}px ${c.width}px 0 0;}\n`;
      decorativeBorderCss += `.corner-bl{bottom:${c.inset.bottom}px;left:${c.inset.left}px;border-width:0 0 ${c.width}px ${c.width}px;}\n`;
      decorativeBorderCss += `.corner-br{bottom:${c.inset.bottom}px;right:${c.inset.right}px;border-width:0 ${c.width}px ${c.width}px 0;}\n`;
      decorativeBorderHtml += '<div class="corner corner-tl"></div>\n<div class="corner corner-tr"></div>\n<div class="corner corner-bl"></div>\n<div class="corner corner-br"></div>\n';
    }
  }

  // Build the HTML document
  const elementsHtml = sortedElements.map(el => elementToHtml(el)).filter(Boolean).join('\n');

  // Embed canvas layout JSON for round-trip editing
  const layoutJson = JSON.stringify(layoutData);
  const layoutComment = `<!--CANVAS_LAYOUT:${layoutJson}:END_CANVAS_LAYOUT-->`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
${fontLinks}
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;${buildBackgroundCss(canvas)};width:${canvas.width}px;height:${canvas.height}px;position:relative;${borderCss};overflow:hidden;}
${decorativeBorderCss}</style>
</head>
<body>
${decorativeBorderHtml}${elementsHtml}
${layoutComment}
</body>
</html>`;
}

/**
 * Extract all {{variable_key}} patterns from canvas elements.
 * Used to populate the allowed_variables field.
 */
export function extractVariablesFromLayout(layoutData: CanvasLayoutData): string[] {
  const variables = new Set<string>();

  layoutData.elements.forEach(el => {
    if (el.type === CanvasElementType.VARIABLE) {
      variables.add((el as VariableCanvasElement).variableKey);
    }
    if (el.type === CanvasElementType.QR_CODE) {
      variables.add((el as QRCodeCanvasElement).dataKey);
    }
  });

  return Array.from(variables);
}
